import { useEffect, useState } from 'react';
import type { AxiosError } from 'axios';
import {
  TaiKhoanNganHang,
  TaiKhoanNganHangInput,
  TaiKhoanNganHangResponse
} from '@/types/schemas/tai-khoan-ngan-hang.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

type BankAccountApiErrorData = Record<string, unknown> & {
  detail?: string;
};

type BankAccountApiError = AxiosError<BankAccountApiErrorData>;

interface UseTaiKhoanNganHangReturn {
  taiKhoanNganHangs: TaiKhoanNganHang[];
  isLoading: boolean;
  error: string | null;
  addTaiKhoanNganHang: (newTaiKhoanNganHang: TaiKhoanNganHangInput) => Promise<TaiKhoanNganHang>;
  updateTaiKhoanNganHang: (uuid: string, updatedTaiKhoanNganHang: TaiKhoanNganHangInput) => Promise<TaiKhoanNganHang>;
  deleteTaiKhoanNganHang: (uuid: string) => Promise<void>;
  refreshTaiKhoanNganHangs: () => Promise<void>;
  getTaiKhoanNganHangByCode: (code: string) => Promise<TaiKhoanNganHang | null>;
}

/**
 * Hook for managing TaiKhoanNganHang (Bank Account) data
 *
 * This hook provides functions to fetch, create, update, and delete bank accounts.
 * It also includes error handling and loading state management.
 */
export const useTaiKhoanNganHang = (initialTaiKhoanNganHangs: TaiKhoanNganHang[] = []): UseTaiKhoanNganHangReturn => {
  const [taiKhoanNganHangs, setTaiKhoanNganHangs] = useState<TaiKhoanNganHang[]>(initialTaiKhoanNganHangs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { entity } = useAuth();

  /**
   * Fetch all bank accounts
   */
  const fetchTaiKhoanNganHangs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<TaiKhoanNganHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`
      );
      setTaiKhoanNganHangs(response.data.results);
    } catch (error: unknown) {
      const apiError = error as BankAccountApiError;
      console.error('Error fetching bank accounts:', apiError);
      setError(apiError.message || 'Có lỗi xảy ra khi tải dữ liệu tài khoản ngân hàng');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Add a new bank account
   *
   * @param newTaiKhoanNganHang - The bank account data to add
   * @returns The added bank account
   */
  const addTaiKhoanNganHang = async (newTaiKhoanNganHang: TaiKhoanNganHangInput): Promise<TaiKhoanNganHang> => {
    if (!entity?.slug) {
      const errorMessage = 'Entity slug is required';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.post<TaiKhoanNganHang>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`,
        newTaiKhoanNganHang
      );
      const addedTaiKhoanNganHang = response.data;

      setTaiKhoanNganHangs(prev => [...prev, addedTaiKhoanNganHang]);
      return addedTaiKhoanNganHang;
    } catch (error: unknown) {
      const apiError = error as BankAccountApiError;
      console.error('Error adding bank account:', apiError);

      // Handle validation errors
      if (apiError.response?.data) {
        let errorMessage = apiError.response.data.detail || '';

        // Handle validation errors which come as an object with field names as keys
        if (typeof apiError.response.data === 'object' && !apiError.response.data.detail) {
          const validationErrors = [];
          for (const field in apiError.response.data) {
            const fieldErrors = apiError.response.data[field];
            if (Array.isArray(fieldErrors)) {
              validationErrors.push(`${field}: ${fieldErrors.join(', ')}`);
            }
          }
          if (validationErrors.length > 0) {
            errorMessage = validationErrors.join('\n');
          } else {
            errorMessage = JSON.stringify(apiError.response.data);
          }
        }

        setError(errorMessage || 'Có lỗi xảy ra khi thêm tài khoản ngân hàng');
        throw new Error(errorMessage || 'Có lỗi xảy ra khi thêm tài khoản ngân hàng');
      }

      setError(apiError.message || 'Có lỗi xảy ra khi thêm tài khoản ngân hàng');
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing bank account
   *
   * @param uuid - The UUID of the bank account to update
   * @param updatedTaiKhoanNganHang - The updated bank account data
   * @returns The updated bank account
   */
  const updateTaiKhoanNganHang = async (
    uuid: string,
    updatedTaiKhoanNganHang: TaiKhoanNganHangInput
  ): Promise<TaiKhoanNganHang> => {
    if (!entity?.slug) {
      const errorMessage = 'Entity slug is required';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.put<TaiKhoanNganHang>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/${uuid}/`,
        updatedTaiKhoanNganHang
      );
      const updatedTaiKhoanNganHangData = response.data;

      setTaiKhoanNganHangs(prev =>
        prev.map(taiKhoanNganHang =>
          taiKhoanNganHang.uuid === updatedTaiKhoanNganHangData.uuid ? updatedTaiKhoanNganHangData : taiKhoanNganHang
        )
      );

      return updatedTaiKhoanNganHangData;
    } catch (error: unknown) {
      const apiError = error as BankAccountApiError;
      console.error('Error updating bank account:', apiError);

      // Handle validation errors
      if (apiError.response?.data) {
        let errorMessage = apiError.response.data.detail || '';

        // Handle validation errors which come as an object with field names as keys
        if (typeof apiError.response.data === 'object' && !apiError.response.data.detail) {
          const validationErrors = [];
          for (const field in apiError.response.data) {
            const fieldErrors = apiError.response.data[field];
            if (Array.isArray(fieldErrors)) {
              validationErrors.push(`${field}: ${fieldErrors.join(', ')}`);
            }
          }
          if (validationErrors.length > 0) {
            errorMessage = validationErrors.join('\n');
          } else {
            errorMessage = JSON.stringify(apiError.response.data);
          }
        }

        setError(errorMessage || 'Có lỗi xảy ra khi cập nhật tài khoản ngân hàng');
        throw new Error(errorMessage || 'Có lỗi xảy ra khi cập nhật tài khoản ngân hàng');
      }

      setError(apiError.message || 'Có lỗi xảy ra khi cập nhật tài khoản ngân hàng');
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a bank account
   *
   * @param uuid - The UUID of the bank account to delete
   */
  const deleteTaiKhoanNganHang = async (uuid: string): Promise<void> => {
    if (!entity?.slug) {
      const errorMessage = 'Entity slug is required';
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    setIsLoading(true);
    setError(null);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/${uuid}/`);
      setTaiKhoanNganHangs(prev => prev.filter(taiKhoanNganHang => taiKhoanNganHang.uuid !== uuid));
    } catch (error: unknown) {
      const apiError = error as BankAccountApiError;
      console.error('Error deleting bank account:', apiError);
      setError(apiError.message || 'Có lỗi xảy ra khi xóa tài khoản ngân hàng');
      throw apiError;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh the list of bank accounts
   */
  const refreshTaiKhoanNganHangs = async (): Promise<void> => {
    if (!entity?.slug) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<TaiKhoanNganHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`
      );
      setTaiKhoanNganHangs(response.data.results);
    } catch (error: unknown) {
      const apiError = error as BankAccountApiError;
      console.error('Error refreshing bank accounts:', apiError);
      setError(apiError.message || 'Có lỗi xảy ra khi tải lại dữ liệu tài khoản ngân hàng');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get a bank account by its code
   *
   * @param code - The bank account code to search for
   * @returns The bank account if found, null otherwise
   */
  const getTaiKhoanNganHangByCode = async (code: string): Promise<TaiKhoanNganHang | null> => {
    if (!entity?.slug) return null;

    try {
      // First check if we already have it in state
      const found = taiKhoanNganHangs.find(item => item.tknh === code);
      if (found) return found;

      // If not found in local state, fetch from API
      const response = await api.get<TaiKhoanNganHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/?tknh=${code}`
      );

      return response.data.results.length > 0 ? response.data.results[0] : null;
    } catch (error: unknown) {
      const apiError = error as BankAccountApiError;
      console.error('Error fetching bank account by code:', apiError);
      setError(apiError.message || 'Có lỗi xảy ra khi tìm kiếm tài khoản ngân hàng');
      return null;
    }
  };

  // Fetch bank accounts on component mount or when entity changes
  useEffect(() => {
    fetchTaiKhoanNganHangs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    taiKhoanNganHangs,
    isLoading,
    error,
    addTaiKhoanNganHang,
    updateTaiKhoanNganHang,
    deleteTaiKhoanNganHang,
    refreshTaiKhoanNganHangs,
    getTaiKhoanNganHangByCode
  };
};
